/**
 * Keyboard shortcuts (E-14, Y-02, docs/08 §8.3).
 *
 * Every action in the platform is reachable without a mouse. Shortcuts are
 * suppressed while the user is typing into a field, except for the explicit
 * modifier combinations that save.
 */

import { useEffect } from "react";

export interface Shortcut {
  key: string;
  ctrl?: boolean;
  shift?: boolean;
  handler: (event: KeyboardEvent) => void;
  /** Fire even when focus is in a text field. Save shortcuts set this. */
  allowInInput?: boolean;
  description: string;
}

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return (
    tag === "INPUT" ||
    tag === "TEXTAREA" ||
    tag === "SELECT" ||
    target.isContentEditable
  );
}

export function useKeyboard(shortcuts: Shortcut[], enabled = true): void {
  useEffect(() => {
    if (!enabled) return;

    const onKeyDown = (event: KeyboardEvent) => {
      const typing = isTypingTarget(event.target);

      for (const shortcut of shortcuts) {
        if (event.key.toLowerCase() !== shortcut.key.toLowerCase()) continue;
        // Cmd on macOS, Ctrl elsewhere.
        if (Boolean(shortcut.ctrl) !== (event.ctrlKey || event.metaKey)) continue;
        if (Boolean(shortcut.shift) !== event.shiftKey) continue;
        if (typing && !shortcut.allowInInput) continue;

        event.preventDefault();
        shortcut.handler(event);
        return;
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [shortcuts, enabled]);
}
